import './styles.scss';

import { Col, Row } from 'react-bootstrap';
import { createComponentState, createPersistentComponentState } from '@/state';

import Button from '@/common/Button';
import { Flag } from '@/models/gen/graphql';
import Modal from '@/common/Modal';
import RadioButton from '@/components/RadioButton';
import { customLocalStorage } from '@/state/storage';
import { getClasses } from '@/utils';
import { searchFlags } from '@/api/services/flag/SearchFlag';
import { useEffect } from 'react';

type EditFlagsModalState = {
  show: boolean;
  value?: Record<string, boolean>;
  /** Called with the final flag selection when the user confirms. */
  onSubmit: (value: Record<string, boolean>) => void;
};
export const useEditFlagsModal = createComponentState<EditFlagsModalState>({
  show: false,
  value: {},
  onSubmit: () => {},
});

type SearchFlagsState = {
  flags: Flag[];
  loading: boolean;
  fetch: () => Promise<void>;
};
const useSearchFlags = createPersistentComponentState<SearchFlagsState>(
  'SearchFlags',
  (setState) => ({
    flags: [],
    loading: false,
    fetch: async () => {
      try {
        setState((current) => ({ ...current, loading: true }));
        const res = await searchFlags();
        setState((current) => ({ ...current, flags: res }));
      } catch (err) {
        console.error('Error fetching flags:', err);
      } finally {
        setState((current) => ({ ...current, loading: false }));
      }
    },
  }),
  {
    storage: customLocalStorage,
  }
);

const EditFlagsModal = () => {
  const [{ flags, loading, fetch }] = useSearchFlags(({ state }) => [state]);
  const [{ show, value }, { onChange, onHide, onDone }] = useEditFlagsModal(({ state, setState }) => [
    state,
    {
      onHide: () => setState((current) => ({ ...current, show: false })),
      onDone: () => {
        const { value: currentValue, onSubmit } = useEditFlagsModal.getState().state;
        onSubmit(currentValue ?? {});
        setState((current) => ({ ...current, show: false }));
      },
      onChange: (flagId: string) => () =>
        setState((current) => ({ ...current, value: { ...current.value, [flagId]: !current.value?.[flagId] } })),
    },
  ]);

  useEffect(() => {
    if (!show || loading) return;
    fetch();
    /* IGNORE OTHER DEPS
    This should only run when the modal is shown, and we don't want it to re-run if flags or loading state changes.
  */ // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  return (
    <Modal className="EditFlagsModal" show={show} onHide={onHide} title="Edit Flags" centered>
      <Row>
        {flags.length === 0 && <h4 className="text-center">No Flags to Show</h4>}
        {flags.length > 0 &&
          (flags || []).map(
            (flag: Flag, f: number): JSX.Element => (
              <Col xs={12} sm={4} md={3} key={f}>
                <RadioButton
                  name={flag.name}
                  onClick={onChange(flag.id)}
                  checked={!!value?.[flag.id]}
                  style={{
                    justifyContent: 'start',
                    borderColor: flag.color,
                  }}
                >
                  <i
                    className={getClasses('sv', value?.[flag.id] ? 'sv-flag-filled' : 'sv-flag-empty', 'fs-4')}
                    style={{ color: flag.color }}
                  />
                  {flag.name}
                </RadioButton>
              </Col>
            )
          )}
      </Row>
      <Row>
        <Col className="d-flex justify-content-end gap-2">
          <Button onClick={onHide} variant="secondary" className="me-2">
            Cancel
          </Button>
          <Button onClick={onDone} disabled={loading} variant="primary">
            Done
          </Button>
        </Col>
      </Row>
    </Modal>
  );
};

export default EditFlagsModal;
